import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Button, Checkbox, Icon, Input, Tooltip, 
} from '@nazka/nazka.mapframe.components';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from 'react-router-dom';

import CompanyIdentificationNumberInput from '@App/components/CompanyIdentificationNumberInput/CompanyIdentificationNumberInput';
import PasswordTooltip from '@App/components/PasswordTooltip/PasswordTooltip';
import PasswordInput from '@App/components/PasswordInput/PasswordInput';
import { register } from '@services/user.service';
import { setShowRegistrationConfirmationModal } from '@App/app.slice';
import { isValidEmail } from '@services/utils.service';

// import CompanyNumberError from './CompanyNumberError/CompanyNumberError';

import { validatePassword } from './register-form.utils';

import './register-form.styl';

function RegisterForm({ setShowLoginModal, group }) {
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState();
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [company, setCompany] = useState();
  const [companyIdentificationNumber, setCompanyIdentificationNumber] = useState([]);
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [passwordCheck, setPasswordCheck] = useState();
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const submitForm = useCallback(async () => {
    if (isLoading) return;
    const errorsArray = [];

    if (!firstName || !firstName.length) {
      errorsArray.push({ location: 'firstName', message: 'Voornaam is niet ingevuld.' })
    }

    if (!lastName || !lastName.length) {
      errorsArray.push({ location: 'lastName', message: 'Achternaam is niet ingevuld.' })
    }

    if (group === 'jobcoaches' && (!company || !company.length)) {
      errorsArray.push({ location: 'company', message: 'Naam van je bedrijf is niet ingevuld.' })
    }

    if (group === 'employers' && (!companyIdentificationNumber || companyIdentificationNumber.length < 1)) {
      errorsArray.push({ location: 'company', message: 'Je moet minstens één ondernemingsnummer opgeven.' })
    }

    if (!email || !email.length) {
      errorsArray.push({ location: 'email', message: 'E-mailadres is niet ingevuld.' })
    } else if (!isValidEmail(email)) {
      errorsArray.push({ location: 'email', message: 'Ongeldig e-mailadres.' })
    }

    validatePassword(password, errorsArray);

    if (!email || !email.length) {
      errorsArray.push({ location: 'email', message: 'E-mailadres is niet ingevuld.' })
    }

    if (password !== passwordCheck) {
      errorsArray.push({ location: 'passwordCheck', message: 'Wachtwoorden komen niet overeen.' })
    }

    if (!termsAccepted) {
      errorsArray.push({ location: 'termsAccepted', message: 'Accepteer de algemene voorwaarden.' })
    }

    // if (group === 'employers' && companyIdentificationNumber.length === 12 && errorsArray.length === 0) {
    //   errorsArray.push({ 
    //     location: 'company', 
    //     message: (
    //       <CompanyNumberError formData={{
    //         firstName, 
    //         lastName,
    //         company: companyIdentificationNumber,
    //         email,
    //         password,
    //         group,
    //       }}
    //       />
    //     ), 
    //   })
    // }

    setErrors(errorsArray);

    if (!errorsArray.length) {
      try {
        setIsLoading(true);
        await register({
          firstName, 
          lastName,
          company: group === 'jobcoaches' ? company : companyIdentificationNumber.map(c => c.id).join(','),
          email,
          password,
          group,
        });
        setIsLoading(false);
        navigate('/');
        dispatch(setShowRegistrationConfirmationModal(true));
      } catch (e) {
        errorsArray.push({ location: 'termsAccepted', message: 'Er is een fout opgetreden bij het verwerken van de aanvraag.' });
        setIsLoading(false);
      }
    }
  }, [
    isLoading,
    firstName,
    lastName,
    group,
    company,
    companyIdentificationNumber,
    email,
    password,
    passwordCheck,
    termsAccepted,
    navigate,
    dispatch,
  ]);

  return (
    <div className="register-form">
      <label className="register-form__field">
        <span className="register-form__field__label">Voornaam</span>
        <Input 
          className="register-form__field__input"
          value={firstName}
          onInputChange={({ target: { value } }) => setFirstName(value)}
          placeholder="voornaam"
        />
      </label>
      {errors.find(e => e.location === 'firstName') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'firstName')?.message}
        </p>
      )}
      <label className="register-form__field">
        <span className="register-form__field__label">Achternaam</span>
        <Input 
          className="register-form__field__input"
          value={lastName}
          onInputChange={({ target: { value } }) => setLastName(value)}
          placeholder="achternaam"
        />
      </label>
      {errors.find(e => e.location === 'lastName') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'lastName')?.message}
        </p>
      )}
      {group === 'jobcoaches' && (
        <label className="register-form__field">
          <span className="register-form__field__label">Naam van je bedrijf</span>
          <Input 
            className="register-form__field__input"
            value={company}
            onInputChange={({ target: { value } }) => setCompany(value)}
            placeholder="bedrijfsnaam"
          />
        </label>
      )}
      {group === 'employers' && (
        <CompanyIdentificationNumberInput
          className="register-form__field"
          onChange={(value) => {
            setErrors(errors.filter(e => e.location !== 'company'));
            setCompanyIdentificationNumber(value)
          }}
          companies={companyIdentificationNumber}
        />
      )}
      {errors.find(e => e.location === 'company') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'company')?.message}
        </p>
      )}
      <label className="register-form__field">
        <span className="register-form__field__label">
          {group === 'jobcoaches' ? 'E-mailadres' 
            : (
              <>
                Zakelijk e-mailadres
                <Tooltip
                  id="e-mailadres-help"
                  clickable
                  tooltip={(
                    <div className="tooltip">
                      We raden je aan om een zakelijk e-mailadres te gebruiken. 
                      Het gebruik van hotmail-, gmail-, telenet- of andere gratis 
                      te verkrijgen e-mailadressen wordt afgeraden.
                    </div>
                  )}
                >
                  <Icon
                    icon={faCircleInfo}
                    size="14px" 
                    rootElProps={{ 'data-tooltip-id': 'e-mailadres-help' }}
                  />
                </Tooltip>
              </>
            )}
        </span>
        <Input 
          className="register-form__field__input"
          value={email}
          onInputChange={({ target: { value } }) => setEmail(value)}
          type="email"
          placeholder="e-mailadres"
        />
      </label>
      {errors.find(e => e.location === 'email') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'email')?.message}
        </p>
      )}
      <label className="register-form__field">
        <span className="register-form__field__label">
          Wachtwoord
          <PasswordTooltip />
        </span>
        <PasswordInput 
          className="register-form__field__input"
          value={password}
          onInputChange={value => setPassword(value)}
          placeholder="wachtwoord"
        />
      </label>
      {errors.find(e => e.location === 'password') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'password')?.message}
        </p>
      )}
      <label className="register-form__field">
        <span className="register-form__field__label">Bevestig wachtwoord</span>
        <PasswordInput 
          className="register-form__field__input"
          value={passwordCheck}
          onInputChange={value => setPasswordCheck(value)}
          placeholder="wachtwoord"
        />
      </label>
      {errors.find(e => e.location === 'passwordCheck') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'passwordCheck')?.message}
        </p>
      )}
      <Checkbox 
        checked={termsAccepted}
        onInputChange={() => setTermsAccepted(!termsAccepted)}
        className="register-form__checkbox"
        text={(
          <span className="register-form__checkbox__label">
            {'Ik ga akkoord met de '}
            <a 
              className="register-form__checkbox__label__url"
              href="https://legal.nazka.be/docs/nl/terms-of-use/"
              target="_blank"
              rel="noreferrer"
            >
              algemene voorwaarden
            </a>
            .
          </span>
        )}
      />
      {errors.find(e => e.location === 'termsAccepted') && (
        <p className="register-form__error">
          {errors.find(e => e.location === 'termsAccepted')?.message}
        </p>
      )}
      <Button 
        className="register-form__button"
        onClick={() => {
          submitForm();
          window.gtag?.('event', 'home_employer_click_register', {
            event_category: 'Account',
            event_label: 'Home employer click register button',
          });
        }}
        isLoading={isLoading}
        disabled={isLoading}
      >
        Registreren
      </Button>
      <Button 
        className="register-form__login"
        layout="ghost"
        hover={false}
        onClick={() => setShowLoginModal(true)}
      >
        Heb je al een account? Meld je aan.
      </Button>
    </div>
  )
}

export default RegisterForm;
